import {z} from 'zod'
import axios, {parseResponseData} from '@/axiosInstance'
import {NotificationPayload, PaginationParams, PaginationResponse, ResponseType} from '@/types'
import {AccessFrequency, PlannedAccesses, ProjectPlannedAccess} from '@/features/planned-accesses/types'
import {captureException} from '@sentry/react'
import {EventTypeKeyName} from '@/features/agenda/types'
import {AccessGroupNameEnum, AccessType, AccessTypeCategoryEnum} from '@/features/access-types/types'

// Show of planned accesses
export const HttpGetPlannedAccessesOptions = z.object({
    urlParams: z.object({
        projectId: z.string()
    })
})
export type HttpGetPlannedAccessesOptions = z.infer<typeof HttpGetPlannedAccessesOptions>

export const httpGetPlannedAccesses = async ({urlParams}: HttpGetPlannedAccessesOptions) => {
    const response = await axios.get(`projects/${urlParams.projectId}/planned-accesses`)
    return parseResponseData({
        responseData: response.data,
        onZodError: captureException,
        responseShape: ProjectPlannedAccess
    })
}

// Index of planned accesses
export type HttpGetPlannedAccessTypesOptions = {
    urlParams: {
        projectId: string
    }
    params: PaginationParams & {
        search?: string
        groupName?: AccessGroupNameEnum
        category?: AccessTypeCategoryEnum
        specializationId?: string
    }
}

const HttpGetPlannedAccessTypesResponse = z.object({
    data: z.array(AccessType),
    meta: PaginationResponse
})
export type HttpGetPlannedAccessTypesResponse = z.infer<typeof HttpGetPlannedAccessTypesResponse>

export const httpGetPlannedAccessTypes = async ({urlParams, params}: HttpGetPlannedAccessTypesOptions) => {
    const response = await axios.get(`projects/${urlParams.projectId}/planned-accesses/access-types`, {params})
    return parseResponseData({
        responseData: response.data,
        onZodError: captureException,
        responseShape: HttpGetPlannedAccessTypesResponse
    })
}

export const HttpGetPlannedAccessesDetailOptions = z.object({
    urlParams: z.object({
        projectId: z.string()
    }),
    params: z.object({
        responseType: ResponseType
    })
})
export type HttpGetPlannedAccessesDetailOptions = z.infer<typeof HttpGetPlannedAccessesDetailOptions>

export const HttpGetPlannedAccessesDetailResponse = z.object({
    in_person_accesses: z.array(
        PlannedAccesses.omit({frequency: true}).extend({accessType: EventTypeKeyName, accessName: z.string()})
    ),
    non_in_person_accesses: z.array(
        PlannedAccesses.omit({frequency: true}).extend({accessType: EventTypeKeyName, accessName: z.string()})
    ),
    treatments: z.array(PlannedAccesses.extend({accessType: EventTypeKeyName, accessName: z.string()})),
    unaccounted_accesses: z.array(
        PlannedAccesses.omit({frequency: true}).extend({accessType: EventTypeKeyName, accessName: z.string()})
    )
})
export type HttpGetPlannedAccessesDetailResponse = z.infer<typeof HttpGetPlannedAccessesDetailResponse>

export const httpGetPlannedAccessesDetail = async ({urlParams, params}: HttpGetPlannedAccessesDetailOptions) => {
    const response = await axios.get(`projects/${urlParams.projectId}/planned-accesses/detail`, {params})
    return parseResponseData({
        responseData: response.data,
        onZodError: captureException,
        responseShape: HttpGetPlannedAccessesDetailResponse
    })
}

// Create planned accesses
export const HttpCreatePlannedAccessesOptions = z.object({
    urlParams: z.object({
        projectId: z.string()
    }),
    payload: z
        .object({
            plannedAccesses: z.array(
                z.object({
                    accessTypeId: z.string(),
                    frequency: AccessFrequency.optional(),
                    numberOfAccesses: z.number().int().positive(),
                    sector: z.enum(['outpatient', 'domiciliary']).optional()
                })
            ),
            treatmentPlan: z.string().optional(),
            targets: z.string().optional(),
            additionalNotes: z.string().optional()
        })
        .extend(NotificationPayload.shape)
})
export type HttpCreatePlannedAccessesOptions = z.infer<typeof HttpCreatePlannedAccessesOptions>

export const httpCreatePlannedAccesses = async ({urlParams, payload}: HttpCreatePlannedAccessesOptions) => {
    const response = await axios.post(`projects/${urlParams.projectId}/planned-accesses`, payload)
    return parseResponseData({
        responseData: response.data,
        onZodError: captureException,
        responseShape: ProjectPlannedAccess
    })
}

// Edit planned accesses
export const HttpEditPlannedAccessesOptions = z.object({
    urlParams: z.object({
        projectId: z.string(),
        id: z.string()
    }),
    payload: z.object({
        plannedAccesses: z.array(
            z.object({
                accessTypeId: z.string(),
                frequency: AccessFrequency.optional(),
                numberOfAccesses: z.number().int().positive(),
                sector: z.enum(['outpatient', 'domiciliary']).optional()
            })
        ),
        treatmentPlan: z.string().optional(),
        targets: z.string().optional(),
        additionalNotes: z.string().optional(),
        notificationRecipientIds: z.array(z.string()).optional(),
        notificationDetails: z.string().optional()
    })
})
export type HttpEditPlannedAccessesOptions = z.infer<typeof HttpEditPlannedAccessesOptions>

export const httpEditPlannedAccesses = async ({urlParams, payload}: HttpEditPlannedAccessesOptions) => {
    const response = await axios.patch(`projects/${urlParams.projectId}/planned-accesses/${urlParams.id}`, payload)
    return parseResponseData({
        responseData: response.data,
        onZodError: captureException,
        responseShape: ProjectPlannedAccess
    })
}
